import type {FeatureRef} from '@src/types/featureCollections.ts';
import {
  createReference,
  parseReference,
} from '@apps-common/geocorn-sdk/api/util';
import {createHash} from 'crypto';

// In these utilities, we often just look at the last 7 chars to extract the last hashid
const HASHID_LENGTH = 7;

export const EMPTY_COLLECTION_ACTIONS_MESSAGE =
  'Upload features to enable collection view actions';

export const getHashFromId = (id?: string) => {
  if (!id) {
    return '';
  }

  return id.slice(-HASHID_LENGTH);
};

export const hashStringSHA256 = (message?: string) => {
  if (!message) {
    return;
  }

  return createHash('sha256').update(message).digest('hex');
};

export const refEquals = (ref1: FeatureRef, ref2: FeatureRef) => {
  if (!ref1 || !ref2) {
    return false;
  }

  const parsedRef1 = parseReference(ref1);
  const parsedRef2 = parseReference(ref2);

  if (
    (parsedRef1.featureId && !parsedRef2.featureId) ||
    (!parsedRef1.featureId && parsedRef2.featureId)
  ) {
    return false;
  }

  return (
    getHashFromId(parsedRef1.featureId) === getHashFromId(parsedRef2.featureId)
  );
};

export const createFullReference = (
  featureHash: string,
  collectionRef: FeatureRef,
) => {
  if (!featureHash || !collectionRef) {
    return undefined;
  }

  const {dataset, collectionId} = parseReference(collectionRef);
  if (!dataset || !collectionId) {
    return undefined;
  }

  return createReference(dataset, collectionId, featureHash);
};

export const createCollectionReference = (featureRef: FeatureRef) => {
  if (!featureRef) {
    return undefined;
  }

  const {dataset, collectionId} = parseReference(featureRef);
  if (!dataset || !collectionId) {
    return undefined;
  }

  // TODO: update createReference in geocorn-sdk to handle refs
  // without features, so we don't have to do the replace here.
  return createReference(dataset, collectionId).replace(/\/undefined$/, '');
};
